import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import CaseStudiesHero from '../components/case-studies/CaseStudiesHero';
import CaseStudyCard from '../components/case-studies/CaseStudyCard';
import CaseStudiesCTA from '../components/case-studies/CaseStudiesCTA';
import { caseStudies } from '../components/case-studies/caseStudiesData';
import {
  organization as orgSchema,
  breadcrumb as breadcrumbSchema,
  SITE_URL,
} from '../components/seo/schemas';
import { Toaster } from '../components/ui/toaster';

const PAGE_URL = `${SITE_URL}/case-studies`;

const buildStructuredData = () => {
  const articles = caseStudies.map((s, idx) => ({
    '@type': 'Article',
    '@id': `${PAGE_URL}#${s.slug}`,
    headline: s.title,
    name: s.title,
    image: s.logo,
    about: s.client,
    articleSection: s.category,
    description: s.challenge,
    articleBody: `Challenge: ${s.challenge} Approach: ${s.approach} Results: ${s.results
      .map((r) => `${r.value} ${r.label}`)
      .join('; ')}. Key takeaway: ${s.takeaway}`,
    keywords: s.services.join(', '),
    position: idx + 1,
    author: { '@id': `${SITE_URL}/#organization` },
    publisher: { '@id': `${SITE_URL}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': PAGE_URL },
    isPartOf: { '@id': `${PAGE_URL}#collection` },
  }));

  const itemList = {
    '@type': 'ItemList',
    name: 'Upscale Growth Marketing Case Studies',
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    numberOfItems: caseStudies.length,
    itemListElement: caseStudies.map((s, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `${PAGE_URL}#${s.slug}`,
      name: s.title,
    })),
  };

  const collectionPage = {
    '@type': 'CollectionPage',
    '@id': `${PAGE_URL}#collection`,
    url: PAGE_URL,
    name: 'Case Studies | Upscale',
    description:
      'Real growth marketing case studies from Upscale: Fishbuddy, ICAN 80 Years, Skida, YM:R Labs and NTL.',
    isPartOf: { '@type': 'WebSite', name: 'Upscale', url: SITE_URL },
    breadcrumb: breadcrumbSchema([
      { name: 'Home', url: SITE_URL },
      { name: 'Case Studies', url: PAGE_URL },
    ]),
    mainEntity: itemList,
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en',
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [orgSchema, collectionPage, ...articles],
  };
};

const CaseStudiesPage = () => {
  const structuredData = buildStructuredData();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Case Studies | Growth Marketing Results - Upscale Oslo"
        description="Explore real growth marketing case studies from Upscale: Fishbuddy App (1M+ downloads), ICAN 80 Years campaign (2.2M TikTok impressions), Skida App, YM:R Labs B2B lead gen, and NTL membership growth (+300%)."
        keywords="growth marketing case studies, app growth case study, performance marketing results, B2B lead generation case study, Fishbuddy case study, ICAN campaign, Skida app marketing, YM:R Labs, NTL membership campaign, Upscale Oslo portfolio"
        canonicalUrl={PAGE_URL}
        ogTitle="Case Studies — Real growth marketing results from Upscale"
        ogDescription="5 client portfolios. App growth, B2B lead gen, non-profit campaigns and membership growth — with the numbers behind the work."
        structuredData={structuredData}
      />
      <Navbar />
      <CaseStudiesHero />

      <section
        data-testid="case-studies-list"
        className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50/40"
      >
        <div className="max-w-6xl mx-auto space-y-12">
          {caseStudies.map((study, index) => (
            <CaseStudyCard key={study.slug} study={study} index={index} />
          ))}
        </div>
      </section>

      <CaseStudiesCTA />
      <Footer />
      <Toaster />
    </div>
  );
};

export default CaseStudiesPage;
